import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import MatchSplitPane from "../components/MatchSplitPane";
import TabSearchFilter, { EMPTY_TAB_FILTER, rowPassesTabFilter } from "../components/TabSearchFilter";
import { SortPicker } from "../components/SortState";
import { ArrowLeft, CheckCircle, AlertTriangle, Link2, ChevronLeft, ChevronRight, FileText, Download, FileCode, FileDown, Layers, Sparkles, X, RotateCw, BarChart3, Flag, ShieldX, Check, Undo2 } from "lucide-react";

const MERGED_TABS = [
  { id: "matched_single",  label: "Matched (single file)"  },
  { id: "matched_conflict",label: "Multi-file Conflicts"   },
  { id: "partial",         label: "Partial"                },
  { id: "manual",          label: "Manually Reconciled"    },
  { id: "unmatched_anchor",label: "Unmatched (Anchor)"     },
  { id: "unmatched_target",label: "Unmatched (Targets)"    },
  { id: "ignored",         label: "Ignored / Non-contributing" },
];

const LEG_TABS = [
  { id: "matched",     label: "Matched"             },
  { id: "partial",     label: "Partial"             },
  { id: "manual",      label: "Manual"              },
  { id: "unmatched_a", label: "Unmatched (Anchor)"  },
  { id: "unmatched_b", label: "Unmatched (Target)"  },
  { id: "ignored",     label: "Ignored / Non-contributing" },
];

const IGNORE_REASONS = [
  { value: "non_contributing", label: "Non-contributing entry" },
  { value: "outside_period", label: "Outside this period" },
  { value: "reversed_later", label: "Reversed in a later period" },
  { value: "already_resolved", label: "Already resolved elsewhere" },
  { value: "bank_fee", label: "Bank fee / charge not in ledger" },
  { value: "other", label: "Other (see remarks)" },
];

const MANUAL_REASONS = [
  { value: "timing_diff",       label: "Timing difference"            },
  { value: "fx_rounding",       label: "FX / rounding difference"     },
  { value: "already_resolved",  label: "Already resolved"             },
  { value: "data_entry_error",  label: "Data entry error"             },
  { value: "missing_ref",       label: "Missing reference / ID rewrite"},
  { value: "partial_payment",   label: "Partial payment / split"      },
  { value: "other",             label: "Other (see remarks)"          },
];

const PAGE_SIZE = 50;

function Card({ label, value, color, sub, icon: Icon }) {
  return (
    <div className="recon-card" style={{ padding: "1rem 1.25rem" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <p style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 600, margin: 0 }}>{label}</p>
          <p style={{ fontSize: "1.6rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, color, margin: "0.2rem 0 0" }}>{value}</p>
          {sub && <p style={{ fontSize: "0.72rem", color: "var(--text-muted)", margin: "0.2rem 0 0" }}>{sub}</p>}
        </div>
        {Icon && <div style={{ width: "2.2rem", height: "2.2rem", borderRadius: "0.5rem", background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={16} style={{ color }} /></div>}
      </div>
    </div>
  );
}

// Normalise legacy multi-run docs where row_a / row_b got double-wrapped as
// { row_a: {...}, row_index_a: N } (and same for b).
function normaliseResultDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const d = { ...doc };
  if (d.row_a && typeof d.row_a === "object" && "row_a" in d.row_a) d.row_a = d.row_a.row_a || {};
  if (d.row_b && typeof d.row_b === "object" && "row_b" in d.row_b) d.row_b = d.row_b.row_b || {};
  return d;
}

function normaliseResultsPayload(payload) {
  if (!payload) return { total: 0, results: [] };
  const results = (payload.results || []).map(r => {
    // matched_conflict groups: {anchor_idx, matches: [...]}
    if (r && Array.isArray(r.matches)) {
      return { ...r, matches: r.matches.map(normaliseResultDoc) };
    }
    return normaliseResultDoc(r);
  });
  return { ...payload, results };
}

function renderRowCompact(row) {
  if (!row) return <em style={{ color: "var(--text-muted)" }}>—</em>;
  const keys = Object.keys(row).filter(k => !k.startsWith("_")).slice(0, 5);
  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "0.5rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.72rem" }}>
      {keys.map((k, i) => {
        const v = row[k];
        const display = v && typeof v === "object" ? JSON.stringify(v) : String(v ?? "");
        return (
          <span key={`${k}-${i}`}><span style={{ color: "var(--text-muted)" }}>{k}:</span> {display}</span>
        );
      })}
    </span>
  );
}

export default function MultiResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mr, setMr] = useState(null);
  const [scope, setScope] = useState("all");
  const [tab, setTab] = useState("matched_single");
  const [data, setData] = useState({ total: 0, results: [] });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resolveOpen, setResolveOpen] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [remarks, setRemarks] = useState("");

  // Manual match state
  const [matchMode, setMatchMode] = useState(false);
  const [legPickerOpen, setLegPickerOpen] = useState(false);
  const [matchTargetLegId, setMatchTargetLegId] = useState(null);
  const [splitPaneA, setSplitPaneA] = useState([]);
  const [splitPaneB, setSplitPaneB] = useState([]);
  const [selectedA, setSelectedA] = useState(new Set());
  const [selectedB, setSelectedB] = useState(new Set());
  const [manualReason, setManualReason] = useState("timing_diff");
  const [manualRemarks, setManualRemarks] = useState("");
  const [submittingMatch, setSubmittingMatch] = useState(false);
  const [matchError, setMatchError] = useState("");

  // Server-side search inputs for the manual-match split panes
  const [splitSearchA, setSplitSearchA] = useState("");
  const [splitSearchB, setSplitSearchB] = useState("");
  const [debouncedSplitA, setDebouncedSplitA] = useState("");
  const [debouncedSplitB, setDebouncedSplitB] = useState("");
  useEffect(() => { const t = setTimeout(() => setDebouncedSplitA(splitSearchA.trim()), 300); return () => clearTimeout(t); }, [splitSearchA]);
  useEffect(() => { const t = setTimeout(() => setDebouncedSplitB(splitSearchB.trim()), 300); return () => clearTimeout(t); }, [splitSearchB]);

  // Partial approve/reject + ignore selection (parity with 2-file ResultsPage).
  // Selection holds recon_results._id strings; we group by run_id at submit time
  // because results span multiple legs under the merged scope.
  const [selectedPartial, setSelectedPartial] = useState(new Set());
  const [partialDecisionOpen, setPartialDecisionOpen] = useState(null); // "approve" | "reject" | null
  const [partialRemarks, setPartialRemarks] = useState("");
  const [submittingPartial, setSubmittingPartial] = useState(false);

  const [selectedIgnore, setSelectedIgnore] = useState(new Set());
  const [ignoreModalOpen, setIgnoreModalOpen] = useState(false);
  const [ignoreReason, setIgnoreReason] = useState("non_contributing");
  const [ignoreRemarks, setIgnoreRemarks] = useState("");
  const [submittingIgnore, setSubmittingIgnore] = useState(false);

  // Tab search filter (one per (scope,tab) reset by change)
  const [tabFilter, setTabFilter] = useState(EMPTY_TAB_FILTER);
  useEffect(() => {
    setTabFilter(EMPTY_TAB_FILTER);
    setSelectedPartial(new Set());
    setSelectedIgnore(new Set());
  }, [scope, tab]);

  // Multi-level sort (server-side, page-agnostic)
  const [sortSpec, setSortSpec] = useState([]);
  useEffect(() => { setSortSpec([]); }, [scope, tab]); // reset on scope/tab change
  const sortToQuery = () => sortSpec.map(s => `${s.path}:${s.dir}`).join(",");

  // Debounced free-text `q` (300ms) — sent server-side so search covers all pages
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ((tabFilter.text || "").trim()), 300);
    return () => clearTimeout(t);
  }, [tabFilter.text]);

  useEffect(() => {
    api.get(`/multi-runs/${id}`).then(r => setMr(r.data)).catch(() => navigate(-1));
  }, [id, navigate]);

  // When scope changes, switch tab to a valid one for that mode
  useEffect(() => {
    if (scope === "all") {
      if (!MERGED_TABS.find(t => t.id === tab)) setTab("matched_single");
    } else {
      if (!LEG_TABS.find(t => t.id === tab)) setTab("matched");
    }
    setPage(0);
    if (matchMode) { setMatchMode(false); setMatchTargetLegId(null); setSplitPaneA([]); setSplitPaneB([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Fetch results — guard against tab/scope mismatch (fixes 400 error)
  useEffect(() => {
    if (!mr) return;
    if (scope === "all"  && !MERGED_TABS.find(t => t.id === tab)) return;
    if (scope !== "all"  && !LEG_TABS.find(t => t.id === tab))    return;
    setLoading(true);
    setData({ total: 0, results: [] }); // reset stale data before fetch to prevent template mismatch crash
    const fetchIt = async () => {
      try {
        const sortQ = sortToQuery();
        if (scope === "all") {
          const params = { tab, skip: page * PAGE_SIZE, limit: PAGE_SIZE };
          if (debouncedQ) params.q = debouncedQ;
          if (sortQ) params.sort = sortQ;
          const r = await api.get(`/multi-runs/${id}/merged-results`, { params });
          setData(normaliseResultsPayload(r.data));
        } else {
          const params = { match_type: tab, skip: page * PAGE_SIZE, limit: PAGE_SIZE };
          if (debouncedQ) params.q = debouncedQ;
          if (sortQ) params.sort = sortQ;
          const r = await api.get(`/reconciliation/runs/${scope}/results`, { params });
          setData(normaliseResultsPayload(r.data));
        }
      } catch {
        setData({ total: 0, results: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchIt();
  }, [id, scope, tab, page, mr, debouncedQ, sortSpec]);

  // Reset page to 0 when debounced search changes
  useEffect(() => { setPage(0); }, [debouncedQ]);

  const refreshAll = async () => {
    const m = await api.get(`/multi-runs/${id}`);
    setMr(m.data);
    if (scope === "all" && MERGED_TABS.find(t => t.id === tab)) {
      const r = await api.get(`/multi-runs/${id}/merged-results`, { params: { tab, skip: page * PAGE_SIZE, limit: PAGE_SIZE } });
      setData(normaliseResultsPayload(r.data));
    } else if (scope !== "all" && LEG_TABS.find(t => t.id === tab)) {
      const r = await api.get(`/reconciliation/runs/${scope}/results`, { params: { match_type: tab, skip: page * PAGE_SIZE, limit: PAGE_SIZE } });
      setData(normaliseResultsPayload(r.data));
    }
  };

  // ---- Manual match ----
  const loadSplitPanes = async (legRunId, qA = "", qB = "") => {
    const paramsA = { match_type: "unmatched_a", limit: 1000 };
    const paramsB = { match_type: "unmatched_b", limit: 1000 };
    if (qA) paramsA.q = qA;
    if (qB) paramsB.q = qB;
    const [ra, rb] = await Promise.all([
      api.get(`/reconciliation/runs/${legRunId}/results`, { params: paramsA }),
      api.get(`/reconciliation/runs/${legRunId}/results`, { params: paramsB }),
    ]);
    setSplitPaneA((ra.data.results || []).map(normaliseResultDoc));
    setSplitPaneB((rb.data.results || []).map(normaliseResultDoc));
  };

  const enterMatchMode = async (legRunId) => {
    setMatchTargetLegId(legRunId);
    setMatchMode(true);
    setLegPickerOpen(false);
    setSelectedA(new Set()); setSelectedB(new Set());
    setSplitSearchA(""); setSplitSearchB("");
    await loadSplitPanes(legRunId, "", "");
  };

  // Re-fetch unmatched rows when split-pane search changes (server-side search)
  useEffect(() => {
    if (!matchMode || !matchTargetLegId) return;
    loadSplitPanes(matchTargetLegId, debouncedSplitA, debouncedSplitB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSplitA, debouncedSplitB, matchMode, matchTargetLegId]);

  const handleMatchModeClick = () => {
    if (scope !== "all") {
      enterMatchMode(scope);
    } else {
      setLegPickerOpen(true);
    }
  };

  const submitManualMatch = async () => {
    if (selState !== "ok") return;
    setSubmittingMatch(true); setMatchError("");
    try {
      await api.post(`/reconciliation/runs/${matchTargetLegId}/manual-match`, {
        a_result_ids: Array.from(selectedA),
        b_result_ids: Array.from(selectedB),
        reason: manualReason,
        remarks: manualRemarks,
      });
      setSelectedA(new Set()); setSelectedB(new Set());
      setManualRemarks("");
      await Promise.all([
        api.get(`/multi-runs/${id}`).then(r => setMr(r.data)),
        loadSplitPanes(matchTargetLegId, debouncedSplitA, debouncedSplitB),
      ]);
    } catch (e) {
      setMatchError(`Manual match failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmittingMatch(false);
    }
  };

  const sa = selectedA.size, sb = selectedB.size;
  const selState = sa === 0 && sb === 0 ? "empty" : sa === 0 || sb === 0 ? "invalid" : (sa > 1 && sb > 1) ? "invalid" : "ok";
  const selMsg = selState === "empty" ? "Select rows on both sides"
    : selState === "invalid" ? (sa > 1 && sb > 1) ? "Ambiguous many-to-many — split into multiple matches" : "Select at least one row on each side"
    : `${sa === 1 && sb === 1 ? "1:1" : sa === 1 ? `1:${sb}` : `${sa}:1`} match ready`;

  const submitResolve = async () => {
    if (!resolveOpen || !chosen) return;
    const other = resolveOpen.matches.filter(m => m.id !== chosen).map(m => m.id);
    try {
      await api.post(`/multi-runs/${id}/resolve-conflict`, { anchor_idx: resolveOpen.anchor_idx, chosen_result_id: chosen, other_result_ids: other, remarks });
      setResolveOpen(null); setChosen(null); setRemarks("");
      refreshAll();
    } catch (e) {
      alert(`Resolve failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // --- Partial approve/reject across (possibly) multiple legs ---------------
  // Backend expects per-run_id POSTs; we group selection by the `run_id` that
  // each recon_results doc carries, then fan out one call per leg.
  const _groupByRun = (rows, idSet) => {
    const byRun = new Map();
    for (const r of rows) {
      if (!idSet.has(r.id)) continue;
      const rid = r.run_id;
      if (!rid) continue;
      if (!byRun.has(rid)) byRun.set(rid, []);
      byRun.get(rid).push(r.id);
    }
    return byRun;
  };

  const submitPartialDecision = async () => {
    if (!partialDecisionOpen || selectedPartial.size === 0) return;
    setSubmittingPartial(true);
    try {
      const byRun = _groupByRun(allRows, selectedPartial);
      if (byRun.size === 0) throw new Error("No rows to decide on");
      await Promise.all(Array.from(byRun.entries()).map(([rid, ids]) =>
        api.post(`/reconciliation/runs/${rid}/partial-decision`, {
          result_ids: ids, decision: partialDecisionOpen, remarks: partialRemarks,
        })
      ));
      setSelectedPartial(new Set());
      setPartialDecisionOpen(null);
      setPartialRemarks("");
      await refreshAll();
    } catch (e) {
      alert(`Decision failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmittingPartial(false);
    }
  };

  const submitIgnore = async () => {
    if (selectedIgnore.size === 0) return;
    setSubmittingIgnore(true);
    try {
      const byRun = _groupByRun(allRows, selectedIgnore);
      if (byRun.size === 0) throw new Error("No rows to ignore");
      await Promise.all(Array.from(byRun.entries()).map(([rid, ids]) =>
        api.post(`/reconciliation/runs/${rid}/ignore-rows`, {
          result_ids: ids, reason: ignoreReason, remarks: ignoreRemarks,
        })
      ));
      setSelectedIgnore(new Set());
      setIgnoreModalOpen(false);
      setIgnoreRemarks("");
      await refreshAll();
    } catch (e) {
      alert(`Ignore failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmittingIgnore(false);
    }
  };

  const undoIgnore = async (row) => {
    try {
      await api.post(`/reconciliation/runs/${row.run_id}/unignore/${row.id}`);
      await refreshAll();
    } catch (e) {
      alert(`Unignore failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const selectedLeg = useMemo(() => {
    if (!mr || scope === "all") return null;
    return mr.legs.find(l => l.leg_run_id === scope) || null;
  }, [mr, scope]);

  const matchTargetLeg = useMemo(() => {
    if (!mr || !matchTargetLegId) return null;
    return mr.legs.find(l => l.leg_run_id === matchTargetLegId) || null;
  }, [mr, matchTargetLegId]);

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const downloadLegFile = (kind) => {
    if (!selectedLeg) return;
    const legId = selectedLeg.leg_run_id;
    const urlMap = {
      csv:  `${apiBase}/api/reconciliation/runs/${legId}/download?match_type=all`,
      html: `${apiBase}/api/reconciliation/runs/${legId}/report`,
      pdf:  `${apiBase}/api/reconciliation/runs/${legId}/pdf`,
    };
    window.open(urlMap[kind], "_blank");
  };

  if (!mr) return <Layout><div style={{ padding: "3rem", textAlign: "center" }}>Loading…</div></Layout>;

  // Build a flattened search-dict per row, then filter
  const flattenResultRow = (r) => {
    if (!r) return {};
    // Conflict groups: search across all matches' target row + target file
    if (Array.isArray(r.matches)) {
      const out = { anchor_idx: r.anchor_idx };
      Object.entries(r.matches[0]?.row_a || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`A:${k}`] = v; });
      r.matches.forEach((m, mi) => {
        out[`target_${mi}`] = m.target_file_name || "";
        out[`rule_${mi}`] = m.rule_name || "";
        Object.entries(m.row_b || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`B${mi}:${k}`] = v; });
      });
      return out;
    }
    const out = {};
    if (r.target_file_name) out.target_file_name = r.target_file_name;
    if (r.rule_name) out.rule_name = r.rule_name;
    if (r.match_type) out.match_type = r.match_type;
    Object.entries(r.row_a || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`A:${k}`] = v; });
    Object.entries(r.row_b || {}).forEach(([k, v]) => { if (!k.startsWith("_")) out[`B:${k}`] = v; });
    return out;
  };

  const allRows = data.results || [];
  // Free-text goes to server (via debouncedQ); only field filter is client-side here
  const clientFilter = { ...tabFilter, text: "" };
  const filteredRows = allRows.filter(r => rowPassesTabFilter(flattenResultRow(r), clientFilter));
  const filterCols = (() => {
    const sample = allRows.find(r => r && !Array.isArray(r.matches));
    if (!sample) return [];
    const cols = new Set();
    if (sample.target_file_name) cols.add("target_file_name");
    if (sample.rule_name) cols.add("rule_name");
    Object.keys(sample.row_a || {}).filter(k => !k.startsWith("_")).forEach(k => cols.add(`A:${k}`));
    Object.keys(sample.row_b || {}).filter(k => !k.startsWith("_")).forEach(k => cols.add(`B:${k}`));
    return [...cols];
  })();

  const s = mr.summary || {};
  // Reconciled % across the multi-run excludes ignored anchor rows from the
  // denominator (parity with 2-file Results page).
  const _denomAnchor = Math.max((s.total_anchor || 0) - (s.ignored_a || 0), 0);
  const matchRate = _denomAnchor ? Math.round(((s.matched_single + s.matched_conflict) / _denomAnchor) * 10000) / 100 : 0;
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const activeTabs = scope === "all" ? MERGED_TABS : LEG_TABS;

  let summaryCards;
  if (scope === "all") {
    const totalIgnored = (s.ignored_a || 0) + (s.ignored_b || 0);
    summaryCards = (
      <>
        <Card label="Anchor Rows"          value={s.total_anchor ?? 0}    color="var(--text-primary)" icon={FileText} />
        <Card label="Matched (single)"     value={s.matched_single ?? 0}  color="#065F46"             icon={CheckCircle} />
        <Card label="Multi-file Conflicts" value={s.matched_conflict ?? 0} color="#B45309"            icon={AlertTriangle} sub="need manual resolution" />
        <Card label="Unmatched Anchor"     value={s.unmatched_anchor ?? 0} color="#991B1B"            sub={`Match rate ${matchRate}%${totalIgnored > 0 ? " · ignored excluded" : ""}`} icon={Link2} />
        <Card label="Partial / Ignored"    value={`${s.partial ?? 0} / ${totalIgnored}`} color="#6B7280" sub={totalIgnored > 0 ? `${s.ignored_a || 0} anchor · ${s.ignored_b || 0} target` : "nothing ignored yet"} icon={ShieldX} />
      </>
    );
  } else if (selectedLeg) {
    const ls = selectedLeg.summary || {};
    const legIgnored = (ls.ignored_a || 0) + (ls.ignored_b || 0);
    const denom = Math.max((ls.total_a || 0) - (ls.ignored_a || 0), 0);
    const legMatchRate = denom ? Math.round(((ls.matched || 0) / denom) * 10000) / 100 : 0;
    summaryCards = (
      <>
        <Card label="Anchor Rows"         value={ls.total_a ?? 0}   color="var(--text-primary)" icon={FileText} />
        <Card label="Target Rows"         value={ls.total_b ?? 0}   color="var(--text-primary)" icon={FileText} />
        <Card label="Matched"             value={ls.matched ?? 0}   color="#065F46"             icon={CheckCircle} sub={`Match rate ${legMatchRate}%${legIgnored > 0 ? " · ignored excluded" : ""}`} />
        <Card label="Partial / Unmatched" value={`${ls.partial ?? 0} / ${(ls.unmatched_a ?? 0) + (ls.unmatched_b ?? 0)}`} color="#B45309" icon={AlertTriangle} />
        <Card label="Ignored"             value={legIgnored} color="#6B7280" icon={ShieldX} sub={legIgnored > 0 ? `${ls.ignored_a || 0} anchor · ${ls.ignored_b || 0} target` : "nothing ignored"} />
      </>
    );
  }

  return (
    <Layout>
      <div className="animate-in">
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={() => navigate(-1)} data-testid="multi-back" style={{ background: "none", border: "none", cursor: "pointer" }}><ArrowLeft size={18} /></button>
            <div>
              <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", margin: 0 }}>{mr.name}</h1>
              <p style={{ color: "var(--text-muted)", fontSize: "0.82rem", margin: 0 }}>
                Anchor: {mr.anchor_file_name} · {mr.legs.length} target file{mr.legs.length === 1 ? "" : "s"} · {new Date(mr.created_at).toLocaleString()}
                {mr.rerun_of && (
                  <span data-testid="multi-rerun-lineage-pill" style={{ marginLeft: "0.5rem", fontSize: "0.7rem", padding: "0.1rem 0.45rem", background: "#EDE9FE", color: "#5B21B6", borderRadius: "9999px", border: "1px solid #C4B5FD", cursor: "pointer" }}
                    onClick={() => navigate(`/multi-results/${mr.rerun_of}`)}
                    title="Open the original multi-run">
                    Rerun of ↩
                  </span>
                )}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <button data-testid="multi-report-btn"
              onClick={() => navigate(`/reports?multi=${id}`)}
              className="btn-secondary"
              title="Build a Debit/Credit report for this multi-run"
              style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
              <BarChart3 size={14} /> Report
            </button>
            <button data-testid="multi-rerun-btn"
            onClick={async () => {
              if (!window.confirm("Re-run this multi-file reconciliation with the same anchor, legs, transform sets and rule sets? A new versioned multi-run will be created.")) return;
              try {
                const r = await api.post(`/multi-runs/${id}/rerun`);
                if (r.data?.id) navigate(`/multi-results/${r.data.id}`);
              } catch (e) {
                alert(`Rerun failed: ${e.response?.data?.detail || e.message}`);
              }
            }}
            className="btn-secondary"
            title="Create a new multi-run with the same configuration"
            style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8rem", padding: "0.375rem 0.875rem" }}>
            <RotateCw size={14} /> Rerun
          </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.75rem", marginBottom: "1rem" }}>
          {summaryCards}
        </div>

        {/* Scope selector */}
        <div className="recon-card" style={{ padding: "0.75rem 1rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.6rem" }}>
            <Layers size={14} style={{ color: "var(--text-muted)" }} />
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Scope</span>
            <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>— select a leg to inspect per-file detail</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
            <button data-testid="scope-all" onClick={() => setScope("all")}
              style={{ padding: "0.5rem 0.85rem", borderRadius: "9999px", border: scope === "all" ? "2px solid var(--sage)" : "1px solid var(--border-default)", background: scope === "all" ? "var(--sage-light)" : "white", color: scope === "all" ? "var(--sage)" : "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>
              All legs (merged)
            </button>
            {mr.legs.map(l => {
              const ls = l.summary || {};
              const active = scope === l.leg_run_id;
              return (
                <button key={l.leg_run_id} data-testid={`scope-leg-${l.leg_run_id}`} onClick={() => setScope(l.leg_run_id)} title={`rule: ${l.rule_set_name}`}
                  style={{ padding: "0.5rem 0.85rem", borderRadius: "9999px", border: active ? "2px solid var(--sage)" : "1px solid var(--border-default)", background: active ? "var(--sage-light)" : "white", color: active ? "var(--sage)" : "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.45rem" }}>
                  <span style={{ fontWeight: 600 }}>{l.target_file_name}</span>
                  <span style={{ fontSize: "0.68rem", color: "#065F46", background: "#D1FAE5", padding: "0.05rem 0.4rem", borderRadius: "9999px", fontWeight: 600 }}>{ls.matched || 0} matched</span>
                  {(ls.partial || 0) > 0 && <span style={{ fontSize: "0.68rem", color: "#B45309", background: "#FEF3C7", padding: "0.05rem 0.4rem", borderRadius: "9999px", fontWeight: 600 }}>{ls.partial} partial</span>}
                </button>
              );
            })}
          </div>

          {selectedLeg && (
            <div style={{ marginTop: "0.75rem", paddingTop: "0.6rem", borderTop: "1px dashed var(--border-default)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                <strong>{selectedLeg.target_file_name}</strong>
                <span style={{ color: "var(--text-muted)" }}> · rule set: <strong style={{ color: "var(--text-secondary)" }}>{selectedLeg.rule_set_name}</strong></span>
              </div>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                <button data-testid="leg-download-csv" onClick={() => downloadLegFile("csv")} className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}><Download size={12} /> CSV</button>
                <button data-testid="leg-download-html" onClick={() => downloadLegFile("html")} className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}><FileCode size={12} /> HTML</button>
                <button data-testid="leg-download-pdf" onClick={() => downloadLegFile("pdf")} className="btn-secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", display: "inline-flex", alignItems: "center", gap: "0.3rem" }}><FileDown size={12} /> PDF</button>
              </div>
            </div>
          )}
        </div>

        <div className="recon-card" style={{ padding: 0 }}>
          <div style={{ borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1rem" }}>
            <div style={{ display: "flex", overflowX: "auto" }}>
              {activeTabs.map(t => (
                <button key={t.id} data-testid={`multi-tab-${t.id}`}
                  onClick={() => { setTab(t.id); setPage(0); setData({ total: 0, results: [] }); if (matchMode) { setMatchMode(false); setMatchTargetLegId(null); } }}
                  style={{ padding: "0.85rem 1rem", fontSize: "0.82rem", fontWeight: 500, border: "none", background: "none", cursor: "pointer", borderBottom: tab === t.id ? "2px solid var(--sage)" : "2px solid transparent", color: tab === t.id ? "var(--sage)" : "var(--text-muted)", whiteSpace: "nowrap" }}>
                  {t.label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {/* Match Mode button — always visible */}
              <button
                data-testid="multi-match-mode-btn"
                onClick={() => matchMode ? (setMatchMode(false), setMatchTargetLegId(null), setSplitPaneA([]), setSplitPaneB([])) : handleMatchModeClick()}
                className={matchMode ? "btn-primary" : "btn-secondary"}
                style={{ fontSize: "0.78rem", padding: "0.35rem 0.8rem", display: "flex", alignItems: "center", gap: "0.35rem", whiteSpace: "nowrap" }}
                title={scope === "all" ? "Click to select which leg to manually match" : "Enter manual match mode for this leg"}
              >
                <Link2 size={13} /> {matchMode ? "Exit Match Mode" : "Match Mode"}
              </button>
              {totalPages > 1 && !matchMode && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                  Page {page + 1} of {totalPages}
                  <button onClick={() => setPage(page - 1)} disabled={page === 0} style={{ background: "none", border: "none", cursor: "pointer", opacity: page === 0 ? 0.3 : 1 }}><ChevronLeft size={14} /></button>
                  <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} style={{ background: "none", border: "none", cursor: "pointer", opacity: page >= totalPages - 1 ? 0.3 : 1 }}><ChevronRight size={14} /></button>
                </div>
              )}
            </div>
          </div>

          {/* Match mode split pane */}
          {matchMode ? (
            <MatchSplitPane
              rowsA={splitPaneA} rowsB={splitPaneB}
              labelA={`Anchor — ${matchTargetLeg ? "All legs" : "Anchor"}`}
              labelB={`Target — ${matchTargetLeg?.target_file_name || "—"}`}
              selectedA={selectedA} setSelectedA={setSelectedA}
              selectedB={selectedB} setSelectedB={setSelectedB}
              searchTextA={splitSearchA} onSearchTextChangeA={setSplitSearchA}
              searchTextB={splitSearchB} onSearchTextChangeB={setSplitSearchB}
            />
          ) : (
            <>
              {(allRows.length > 0 || debouncedQ) && (
                <TabSearchFilter
                  filter={tabFilter}
                  onChange={setTabFilter}
                  columns={filterCols}
                  testidPrefix="multi-tab-filter"
                  visible={filteredRows.length}
                  total={data.total || 0}
                />
              )}
              {filterCols.length > 0 && (
                <div style={{ padding: "0.4rem 0.875rem", borderBottom: "1px solid var(--border-default)", background: "var(--bg-subtle, #FAFAF7)", display: "flex", flexWrap: "wrap" }}>
                  <SortPicker
                    columns={filterCols.map(c => ({ value: c, label: c }))}
                    spec={sortSpec}
                    setSpec={setSortSpec}
                    testidPrefix="multi-sort"
                  />
                </div>
              )}
              <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {loading ? (
                  <div style={{ padding: "3rem", textAlign: "center" }}>
                    <div style={{ width: "1.5rem", height: "1.5rem", border: "2px solid var(--sage)", borderTop: "2px solid transparent", borderRadius: "9999px", animation: "spin 0.8s linear infinite", margin: "0 auto" }} />
                  </div>
                ) : allRows.length === 0 ? (
                  <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                    {(debouncedQ || tabFilter.value) ? "No records match this filter" : "No records in this category"}
                  </div>
                ) : filteredRows.length === 0 ? (
                  <div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.85rem" }}>No records match this filter</div>
                ) : scope === "all" && tab === "matched_conflict" ? (
                  <div style={{ padding: "0.75rem 1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                    {filteredRows.map((g, gi) => (
                      <div key={`${g.anchor_idx}-${gi}`} data-testid={`conflict-${g.anchor_idx}`} style={{ border: "1px solid #FDE68A", borderRadius: "0.5rem", padding: "0.75rem 1rem", background: "#FFFBEB" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                            Anchor row #{g.anchor_idx} matched <span style={{ color: "#B45309" }}>{(g.matches || []).length}</span> files
                          </div>
                          <button data-testid={`resolve-${g.anchor_idx}`} onClick={() => { setResolveOpen(g); setChosen(null); setRemarks(""); }} className="btn-primary" style={{ fontSize: "0.78rem", padding: "0.35rem 0.75rem" }}>Resolve</button>
                        </div>
                        <div style={{ padding: "0.4rem", background: "white", borderRadius: "0.35rem", marginBottom: "0.4rem" }}>
                          <div style={{ fontSize: "0.7rem", color: "var(--sage)", fontWeight: 700, marginBottom: "0.2rem" }}>ANCHOR</div>
                          {renderRowCompact(g.matches[0]?.row_a)}
                        </div>
                        {(g.matches || []).map(m => (
                          <div key={m.id} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", padding: "0.4rem", borderTop: "1px solid #FDE68A" }}>
                            <div style={{ fontSize: "0.7rem", color: "#1E40AF", fontWeight: 700, minWidth: "110px" }}>
                              {m.target_file_name}<br />
                              <span style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 400 }}>rule: {m.rule_name}</span>
                            </div>
                            <div style={{ flex: 1 }}>{renderRowCompact(m.row_b)}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  (() => {
                    const showPartialCheckbox = tab === "partial";
                    const showIgnoreCheckbox  = ["unmatched_anchor", "unmatched_target", "unmatched_a", "unmatched_b"].includes(tab);
                    const isIgnoredTab        = tab === "ignored";
                    return (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            {(showPartialCheckbox || showIgnoreCheckbox) && (
                              <th className="table-header" style={{ width: "2.5rem" }}>
                                <input
                                  type="checkbox"
                                  data-testid="multi-bulk-select"
                                  checked={filteredRows.length > 0 && filteredRows.every(r => (showPartialCheckbox ? selectedPartial : selectedIgnore).has(r.id))}
                                  onChange={() => {
                                    const store = showPartialCheckbox ? selectedPartial : selectedIgnore;
                                    const setter = showPartialCheckbox ? setSelectedPartial : setSelectedIgnore;
                                    const next = new Set(store);
                                    const allSelected = filteredRows.length > 0 && filteredRows.every(r => store.has(r.id));
                                    filteredRows.forEach(r => allSelected ? next.delete(r.id) : next.add(r.id));
                                    setter(next);
                                  }}
                                />
                              </th>
                            )}
                            {scope === "all" && <th className="table-header">Target file</th>}
                            <th className="table-header">Rule</th>
                            <th className="table-header">Anchor row</th>
                            <th className="table-header">Target row</th>
                            {isIgnoredTab && <th className="table-header" style={{ textAlign: "right" }}>Ignored</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map(r => {
                            const selStore = showPartialCheckbox ? selectedPartial : selectedIgnore;
                            const setSel   = showPartialCheckbox ? setSelectedPartial : setSelectedIgnore;
                            const checked  = selStore.has(r.id);
                            return (
                              <tr key={r.id} className="table-row" data-testid={`result-row-${r.id}`}>
                                {(showPartialCheckbox || showIgnoreCheckbox) && (
                                  <td className="table-cell" style={{ width: "2.5rem" }}>
                                    <input type="checkbox"
                                      data-testid={`row-select-${r.id}`}
                                      checked={checked}
                                      onChange={() => {
                                        const next = new Set(selStore);
                                        if (checked) next.delete(r.id); else next.add(r.id);
                                        setSel(next);
                                      }} />
                                  </td>
                                )}
                                {scope === "all" && <td className="table-cell" style={{ fontSize: "0.78rem", color: "#1E40AF" }}>{r.target_file_name || "—"}</td>}
                                <td className="table-cell" style={{ fontSize: "0.78rem" }}>
                                  {r.match_type === "manual"
                                    ? <span style={{ background: "#EDE9FE", color: "#5B21B6", padding: "0.1rem 0.45rem", borderRadius: "9999px", border: "1px solid #C4B5FD", fontSize: "0.72rem", fontWeight: 600 }}>Manual</span>
                                    : r.match_type === "ignored_a" || r.match_type === "ignored_b"
                                    ? <span style={{ background: "#F3F4F6", color: "#4B5563", padding: "0.1rem 0.45rem", borderRadius: "9999px", border: "1px solid #D1D5DB", fontSize: "0.72rem", fontWeight: 600 }}>
                                        Ignored {r.match_type === "ignored_a" ? "(A)" : "(B)"}
                                      </span>
                                    : <span style={{ background: "var(--sage-light)", color: "var(--sage)", padding: "0.1rem 0.45rem", borderRadius: "9999px", border: "1px solid var(--sage-border)", fontSize: "0.72rem" }}>{r.rule_name || "—"}</span>
                                  }
                                </td>
                                <td className="table-cell">{renderRowCompact(r.row_a)}</td>
                                <td className="table-cell">{renderRowCompact(r.row_b)}</td>
                                {isIgnoredTab && (
                                  <td className="table-cell" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: "0.2rem" }}>
                                      {r.ignore_reason && (
                                        <span style={{ fontSize: "0.7rem", color: "#4B5563" }}>
                                          {r.ignore_reason}{r.ignored_by === "rule" ? " · by rule" : r.ignored_by_email ? ` · by ${r.ignored_by_email}` : ""}
                                        </span>
                                      )}
                                      {r.ignore_remarks && (
                                        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontStyle: "italic", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis" }}>"{r.ignore_remarks}"</span>
                                      )}
                                      <button
                                        data-testid={`unignore-${r.id}`}
                                        onClick={() => undoIgnore(r)}
                                        className="btn-secondary"
                                        style={{ fontSize: "0.7rem", padding: "0.2rem 0.55rem", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
                                        title="Move back to Unmatched">
                                        <Undo2 size={11} /> Unignore
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Floating action bar in match mode */}
      {matchMode && (
        <div data-testid="multi-manual-match-action-bar"
          style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "white", borderTop: "1px solid var(--sage-border)", padding: "0.75rem 1.5rem", zIndex: 45, boxShadow: "0 -8px 24px rgba(92,122,94,0.10)", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 600, padding: "0.35rem 0.75rem", borderRadius: "9999px", background: selState === "ok" ? "#D1FAE5" : selState === "invalid" ? "#FEE2E2" : "#F3F4F6", color: selState === "ok" ? "#065F46" : selState === "invalid" ? "#991B1B" : "#4B5563" }}>
            <Sparkles size={12} style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "middle" }} />
            {selMsg}
          </div>
          {matchTargetLeg && (
            <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Leg: <strong style={{ color: "var(--text-primary)" }}>{matchTargetLeg.target_file_name}</strong>
            </span>
          )}
          <select data-testid="multi-manual-reason" className="recon-input" value={manualReason} onChange={e => setManualReason(e.target.value)} style={{ fontSize: "0.82rem", minWidth: "220px" }}>
            {MANUAL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <input data-testid="multi-manual-remarks" type="text" className="recon-input" value={manualRemarks} onChange={e => setManualRemarks(e.target.value)}
            placeholder={manualReason === "other" ? "Explain (required for Other)" : "Remarks (optional)"}
            style={{ flex: 1, minWidth: "200px", fontSize: "0.82rem" }} />
          <button data-testid="multi-manual-match-submit" className="btn-primary"
            disabled={selState !== "ok" || submittingMatch || (manualReason === "other" && !manualRemarks.trim())}
            onClick={submitManualMatch}
            style={{ opacity: (selState !== "ok" || submittingMatch || (manualReason === "other" && !manualRemarks.trim())) ? 0.5 : 1, fontSize: "0.85rem", padding: "0.45rem 1.1rem", display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <Link2 size={14} /> {submittingMatch ? "Matching…" : "Match selected"}
          </button>
          <button onClick={() => { setSelectedA(new Set()); setSelectedB(new Set()); setManualRemarks(""); setMatchError(""); }} className="btn-secondary" style={{ fontSize: "0.82rem", padding: "0.4rem 0.9rem" }}>Clear</button>
          {matchError && (
            <div data-testid="multi-match-error" style={{ flexBasis: "100%", fontSize: "0.78rem", color: "#991B1B", background: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: "0.375rem", padding: "0.4rem 0.6rem", marginTop: "0.25rem" }}>
              {matchError}
            </div>
          )}
        </div>
      )}

      {/* Leg picker modal (shown when match mode clicked in "all" scope) */}
      {legPickerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setLegPickerOpen(false)} />
          <div data-testid="leg-picker-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "480px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>Select leg to match against</h3>
              <button onClick={() => setLegPickerOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={16} /></button>
            </div>
            <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.75rem" }}>
              Choose which target file's unmatched rows to manually match with unmatched anchor rows.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {mr.legs.map(l => {
                const ls = l.summary || {};
                return (
                  <button key={l.leg_run_id} data-testid={`pick-leg-${l.leg_run_id}`}
                    onClick={() => enterMatchMode(l.leg_run_id)}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0.875rem", border: "1px solid var(--border-default)", borderRadius: "0.5rem", background: "white", cursor: "pointer", textAlign: "left" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{l.target_file_name}</div>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>rule: {l.rule_set_name}</div>
                    </div>
                    <div style={{ display: "flex", gap: "0.35rem", fontSize: "0.68rem" }}>
                      <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "0.1rem 0.4rem", borderRadius: "9999px", fontWeight: 600 }}>{(ls.unmatched_a || 0) + (ls.unmatched_b || 0)} unmatched</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Resolve conflict modal */}
      {resolveOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setResolveOpen(null)} />
          <div data-testid="resolve-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "640px", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: "0 0 0.25rem" }}>Resolve multi-file conflict</h3>
            <p style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.75rem" }}>
              Pick which target file is the real match for anchor row #{resolveOpen.anchor_idx}. The others will be marked as unmatched on their respective legs.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginBottom: "0.9rem" }}>
              {resolveOpen.matches.map(m => (
                <label key={m.id} data-testid={`resolve-opt-${m.id}`}
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", border: chosen === m.id ? "2px solid var(--sage)" : "1px solid var(--border-default)", borderRadius: "0.5rem", padding: "0.55rem 0.75rem", cursor: "pointer", background: chosen === m.id ? "var(--sage-light)" : "white" }}>
                  <input type="radio" name="resolve" checked={chosen === m.id} onChange={() => setChosen(m.id)} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.82rem", color: "#1E40AF" }}>{m.target_file_name} <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 400 }}>· rule: {m.rule_name}</span></div>
                    <div style={{ marginTop: "0.2rem" }}>{renderRowCompact(m.row_b)}</div>
                  </div>
                </label>
              ))}
            </div>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Remarks (optional)</label>
            <textarea data-testid="resolve-remarks" className="recon-input" value={remarks} onChange={e => setRemarks(e.target.value)}
              style={{ width: "100%", fontSize: "0.85rem", minHeight: "70px", fontFamily: "inherit", marginBottom: "0.9rem" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setResolveOpen(null)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="resolve-submit" onClick={submitResolve} disabled={!chosen} className="btn-primary" style={{ fontSize: "0.85rem", opacity: chosen ? 1 : 0.5 }}>Resolve</button>
            </div>
          </div>
        </div>
      )}
      {/* Partial approve/reject action bar (parity with 2-file Results) */}
      {tab === "partial" && selectedPartial.size > 0 && !partialDecisionOpen && !matchMode && (
        <div data-testid="multi-partial-action-bar"
          style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "white", borderTop: "1px solid #FDE68A", padding: "0.75rem 1.5rem", zIndex: 40, boxShadow: "0 -8px 24px rgba(180,130,20,0.12)", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 600, padding: "0.35rem 0.75rem", borderRadius: "9999px", background: "#FEF3C7", color: "#92400E" }}>
            <Flag size={12} style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "middle" }} />
            {selectedPartial.size} partial row{selectedPartial.size === 1 ? "" : "s"} selected
          </div>
          <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>across {new Set(allRows.filter(r => selectedPartial.has(r.id)).map(r => r.run_id)).size} leg(s)</span>
          <button data-testid="multi-partial-approve-btn" onClick={() => setPartialDecisionOpen("approve")} className="btn-primary"
            style={{ fontSize: "0.85rem", padding: "0.45rem 1.1rem", display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <Check size={14} /> Approve as Manual
          </button>
          <button data-testid="multi-partial-reject-btn" onClick={() => setPartialDecisionOpen("reject")}
            className="btn-secondary"
            style={{ fontSize: "0.85rem", padding: "0.45rem 1.1rem", display: "flex", alignItems: "center", gap: "0.375rem", color: "#991B1B", borderColor: "#FCA5A5" }}>
            <X size={14} /> Reject (split to Unmatched)
          </button>
          <button onClick={() => setSelectedPartial(new Set())} className="btn-secondary" style={{ fontSize: "0.82rem", padding: "0.4rem 0.9rem" }}>Clear</button>
        </div>
      )}

      {/* Ignore action bar on unmatched tabs */}
      {["unmatched_anchor", "unmatched_target", "unmatched_a", "unmatched_b"].includes(tab) && selectedIgnore.size > 0 && !ignoreModalOpen && !matchMode && (
        <div data-testid="multi-ignore-action-bar"
          style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "white", borderTop: "1px solid #D1D5DB", padding: "0.75rem 1.5rem", zIndex: 40, boxShadow: "0 -8px 24px rgba(75,85,99,0.12)", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 600, padding: "0.35rem 0.75rem", borderRadius: "9999px", background: "#F3F4F6", color: "#4B5563" }}>
            <ShieldX size={12} style={{ display: "inline", marginRight: "0.3rem", verticalAlign: "middle" }} />
            {selectedIgnore.size} row{selectedIgnore.size === 1 ? "" : "s"} selected
          </div>
          <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
            across {new Set(allRows.filter(r => selectedIgnore.has(r.id)).map(r => r.run_id)).size} leg(s) — ignored rows are excluded from reconciled %
          </span>
          <button data-testid="multi-ignore-btn" onClick={() => setIgnoreModalOpen(true)} className="btn-primary"
            style={{ fontSize: "0.85rem", padding: "0.45rem 1.1rem", display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <ShieldX size={14} /> Mark as Ignored
          </button>
          <button onClick={() => setSelectedIgnore(new Set())} className="btn-secondary" style={{ fontSize: "0.82rem", padding: "0.4rem 0.9rem" }}>Clear</button>
        </div>
      )}

      {/* Partial decision modal */}
      {partialDecisionOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setPartialDecisionOpen(null)} />
          <div data-testid="multi-partial-decision-modal"
            style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>
                {partialDecisionOpen === "approve" ? "Approve partial matches" : "Reject partial matches"}
              </h3>
              <button onClick={() => setPartialDecisionOpen(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={16} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0.25rem 0 0.75rem" }}>
              {partialDecisionOpen === "approve"
                ? `${selectedPartial.size} partial row(s) will be promoted to Manual matches across their respective legs.`
                : `${selectedPartial.size} partial row(s) will be split into Unmatched A and Unmatched B records.`}
            </p>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Remarks (optional)</label>
            <textarea data-testid="multi-partial-remarks" value={partialRemarks} onChange={e => setPartialRemarks(e.target.value)} className="recon-input"
              placeholder={partialDecisionOpen === "approve" ? "Why are these OK?" : "Why are these not a match?"}
              style={{ width: "100%", fontSize: "0.85rem", minHeight: "70px", fontFamily: "inherit", marginBottom: "0.9rem" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setPartialDecisionOpen(null)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="multi-partial-decision-submit"
                onClick={submitPartialDecision}
                disabled={submittingPartial}
                className="btn-primary"
                style={{ fontSize: "0.85rem", opacity: submittingPartial ? 0.6 : 1 }}>
                {submittingPartial ? "Working…" : (partialDecisionOpen === "approve" ? "Approve" : "Reject")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ignore modal */}
      {ignoreModalOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setIgnoreModalOpen(false)} />
          <div data-testid="multi-ignore-modal"
            style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.5rem", width: "100%", maxWidth: "440px", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.25rem" }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.05rem", margin: 0 }}>Ignore / mark non-contributing</h3>
              <button onClick={() => setIgnoreModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={16} /></button>
            </div>
            <p style={{ fontSize: "0.82rem", color: "var(--text-secondary)", margin: "0.25rem 0 0.75rem" }}>
              {selectedIgnore.size} row(s) will move to the <strong>Ignored</strong> tab on their respective legs. They'll be excluded from reconciled % but still counted as fully handled under Effective Coverage.
            </p>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Reason</label>
            <select data-testid="multi-ignore-reason" value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)} className="recon-input"
              style={{ width: "100%", marginBottom: "0.75rem", fontSize: "0.85rem" }}>
              {IGNORE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.25rem" }}>Remarks (optional)</label>
            <textarea data-testid="multi-ignore-remarks" value={ignoreRemarks} onChange={e => setIgnoreRemarks(e.target.value)} className="recon-input"
              placeholder="e.g. 'Bank fee — reversed in March period'"
              style={{ width: "100%", fontSize: "0.85rem", minHeight: "70px", fontFamily: "inherit", marginBottom: "0.9rem" }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setIgnoreModalOpen(false)} className="btn-secondary" style={{ fontSize: "0.85rem" }}>Cancel</button>
              <button data-testid="multi-ignore-submit"
                onClick={submitIgnore}
                disabled={submittingIgnore}
                className="btn-primary"
                style={{ fontSize: "0.85rem", opacity: submittingIgnore ? 0.6 : 1 }}>
                {submittingIgnore ? "Working…" : "Ignore"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
